import { StepperWithSuccessPage } from '@/components'
import { AssessmentType, ThemeColor } from '@/enums'
import { useContactModal } from '@/providers'
import { theme } from '@/theme'
import { useLanguage } from '@/utils'
import { Button } from '@surein/ui'
import { Phone } from 'lucide-react'
import { useCallback, ReactNode } from 'react'
import {
  AssessmentValuesProvider,
  makeSteps,
  makeStepsWithBreadcrumbs,
} from './helpers'
import { AssessmentSteps } from './steps'
import { StepWrapper, StepWrapperMessages } from './steps/StepWrapper'
import { Container } from './styles'

interface HideContactOptions {
  chat?: boolean
  header?: boolean
}

interface AssessmentMessages extends StepWrapperMessages {
  assessmentSuccessButton: string
  assessmentSuccessSubtitle: string
  assessmentSuccessTitle: string
}

interface AssessmentStepperProps {
  initialValues: Record<string, any>
  type?: AssessmentType
  initialStep?: string
  onLogoClick?: () => void
  onFinish: () => void
  stepKeys: string[]
  stepProps?: Record<string, any>
  hideLastStepButton?: boolean
  skipSuccessPage?: boolean
  stepLocalStorageKey?: string
  valuesLocalStorageKey?: string
  hideContact?: HideContactOptions
  messages: AssessmentMessages
  stepperBreadcrumbTitles?: Record<string, any>
}

interface StepWrapperProps {
  messages: AssessmentMessages
  stepLocalStorageKey?: string
  valuesLocalStorageKey?: string
  step: string
  children: ReactNode
}

export const AssessmentStepper = ({
  initialValues,
  type,
  initialStep,
  onLogoClick,
  onFinish,
  stepKeys,
  stepProps,
  hideLastStepButton,
  skipSuccessPage,
  stepLocalStorageKey,
  valuesLocalStorageKey,
  messages,
  stepperBreadcrumbTitles,
}: AssessmentStepperProps) => {
  const { openContact } = useContactModal()

  // TODO there seems to be a type mismatch here. fix it when removing ramda
  const steps = stepperBreadcrumbTitles
    ? makeStepsWithBreadcrumbs(AssessmentSteps, stepKeys as any)
    : makeSteps(AssessmentSteps)(stepKeys as any)
  const language = useLanguage()

  const MemoizedStepWrapper = useCallback(
    (props: StepWrapperProps) => (
      <StepWrapper
        {...props}
        messages={messages}
        stepLocalStorageKey={stepLocalStorageKey}
        valuesLocalStorageKey={valuesLocalStorageKey}
      />
    ),
    [language, stepLocalStorageKey, valuesLocalStorageKey],
  )

  return (
    <Container>
      <AssessmentValuesProvider initialValue={initialValues}>
        <StepperWithSuccessPage
          hideLastStepButton={hideLastStepButton}
          initialStep={initialStep}
          onFinish={onFinish}
          onLogoClick={onLogoClick}
          shouldUpdateUrl={type === AssessmentType.firstTimeUser}
          skipSuccessPage={skipSuccessPage}
          stepperBreadcrumbTitles={stepperBreadcrumbTitles}
          stepProps={stepProps}
          steps={steps}
          stepWrapper={MemoizedStepWrapper}
          subtitle={messages.assessmentSuccessSubtitle}
          successButtonText={messages.assessmentSuccessButton}
          title={messages.assessmentSuccessTitle}
          topRight={() => (
            <Button
              icon={() => (
                <Phone color={theme.color[ThemeColor.success1]} size={16} />
              )}
              onClick={openContact}
              size="sm"
              variant="muted"
            />
          )}
        />
      </AssessmentValuesProvider>
    </Container>
  )
}
